import { type ReactNode } from 'react';
import { DEFAULT_REMINDER_LEAD_MINUTES } from '@/constants/Maintenance/maintenance';
import { useMaintenance } from '@/contexts/MaintenanceContext';
import MaintenanceAlertBar from './MaintenanceAlertBar';
import MaintenanceModal from './MaintenanceModal';
import MaintenancePage from './MaintenancePage';
import useMaintenanceReminderSnackbar from './useMaintenanceReminderSnackbar';

interface IProps {
  children: ReactNode;
}

/**
 * Decides which maintenance surface the current user should see and wraps the
 * rest of the app with it.
 */
export default function MaintenanceGate({ children }: IProps): ReactNode {
  const {
    phase,
    maintenanceWindow,
    isLockedOut,
    isPopupOpen,
    isReminderOpen,
    dismissPopup,
    dismissReminder,
  } = useMaintenance();

  useMaintenanceReminderSnackbar({
    isOpen: Boolean(maintenanceWindow) && !isLockedOut && isReminderOpen,
    startsAt: maintenanceWindow?.startsAt ?? '',
    expectedEndAt: maintenanceWindow?.expectedEndAt ?? '',
    leadMinutes: maintenanceWindow?.reminderLeadMinutes ?? DEFAULT_REMINDER_LEAD_MINUTES,
    onDismiss: dismissReminder,
  });

  const renderContent = (): ReactNode => {
    if (!maintenanceWindow) return children;

    if (isLockedOut) {
      return (
        <MaintenancePage
          startsAt={maintenanceWindow.startsAt}
          expectedEndAt={maintenanceWindow.expectedEndAt}
          description={maintenanceWindow.description}
        />
      );
    }

    return (
      <>
        {phase && (
          <MaintenanceAlertBar
            phase={phase}
            startsAt={maintenanceWindow.startsAt}
            expectedEndAt={maintenanceWindow.expectedEndAt}
            leadMinutes={maintenanceWindow.reminderLeadMinutes}
          />
        )}

        {children}
      </>
    );
  };

  return (
    <>
      {renderContent()}

      <MaintenanceModal
        open={Boolean(maintenanceWindow) && !isLockedOut && isPopupOpen}
        startsAt={maintenanceWindow?.startsAt ?? ''}
        expectedEndAt={maintenanceWindow?.expectedEndAt ?? ''}
        onDismiss={dismissPopup}
      />
    </>
  );
}
